'use client';
import { RestApiDayAvailabilityDto, RestApiDeliveryMethodDto } from '@order-mage/integration-sdk';
import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { ComponentPropsWithoutRef, useContext } from 'react';
import { useWatch } from 'react-hook-form';
import { useCartStore, CartContext, getPriceForItem, getPriceValueTranslation } from '@/entities/cart';
import { useDeliveryMethodAvailability } from '@/entities/venue/lib/useDeliveryMethodAvailability';
import { QueryApi } from '@/features/query/api';
import { cn } from '@/shared/lib/cn';
import { useGetLocalizedValue } from '@/shared/lib/hooks/useGetLocalizedValue';
import { useSelectedCurrency } from '@/shared/lib/hooks/useSelectedCurrency';
import { Country } from '@/shared/model/Country';
import { Badge } from '@/shared/ui/badge';
import { Form } from '@/shared/ui/form';
import { Icon } from '@/shared/ui/icon';
import { InputRow } from '@/shared/ui/input-row';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const IconMap: Record<string, any> = {
    car: (svgProps: ComponentPropsWithoutRef<'svg'>) => <Icon name="car" {...svgProps} />,
    'map-pin': (svgProps: ComponentPropsWithoutRef<'svg'>) => <Icon name="map-pin" {...svgProps} />,
};

type DeliveryMethodWithTimeAvailability = RestApiDeliveryMethodDto & {
    timeAvailability: RestApiDayAvailabilityDto[];
};

export const CartDeliveryMethodsList = ({ methods }: { methods: DeliveryMethodWithTimeAvailability[] }) => {
    const t = useTranslations();
    const venueQuery = useQuery(QueryApi.Venue.queries.Venue);
    const [selectedCurrency] = useSelectedCurrency();
    const getLocalizedValue = useGetLocalizedValue();
    const getDeliveryMethodAreaPrice = useCartStore((s) => s.getDeliveryMethodAreaPrice);
    const { getAvailability } = useDeliveryMethodAvailability();
    const { formMethods } = useContext(CartContext);
    const venueCountry = (venueQuery.data?.address?.country as Country) ?? Country.CZ;
    const [lat, lon] = useWatch({
        control: formMethods.control,
        name: ['deliveryMethod.address.lat', 'deliveryMethod.address.lon'],
    });
    return (
        <Form.RadioGroup
            name="deliveryMethod.id"
            className="cart-delivery-box__radio-group"
            onValueChange={() => {
                // Only clear a timed slot — avoid three setValues when already on earliest.
                const time = formMethods.getValues('time');
                if (time.type === 'timed' || time.timed?.date || time.timed?.timeStart) {
                    formMethods.setValue('time', { type: 'earliest', timed: null });
                }
            }}
        >
            {({ value }) => (
                <>
                    {methods.map((method) => {
                        const { id, name, taxedPrice } = method;
                        const icon = method.deliveryMethodDefinition?.icon;
                        const deliveryMethodPrice = getPriceForItem(
                            { prices: [taxedPrice] },
                            venueCountry,
                            selectedCurrency,
                        );
                        const areaPrice =
                            !!lat && !!lon
                                ? getDeliveryMethodAreaPrice(method, parseFloat(lat), parseFloat(lon))
                                : null;
                        const {
                            isAddressRequired,
                            isUndeliverable,
                            isMinOrderDisabled,
                            isTimeRestricted,
                            isDisabled,
                            minPrice,
                            areaTimeRestrictionLabel,
                        } = getAvailability(method);

                        const totalPrice =
                            parseFloat(deliveryMethodPrice?.amount || '0') +
                            parseFloat(areaPrice?.amount || '0');

                        return (
                            <InputRow
                                key={id}
                                as={Form.RadioGroup.Item}
                                value={id}
                                isActive={value === id}
                                disabled={isDisabled}
                                data-testid="cart-delivery-box-delivery-method"
                                data-id={id}
                            >
                                <Form.RadioGroup.Indicator />
                                {(() => {
                                    const IconComponent = IconMap[icon ?? 'map-pin'] ?? IconMap['map-pin'];
                                    return <IconComponent width={24} height={24} data-icon={icon} />;
                                })()}
                                <span
                                    className="cart-delivery-box__text"
                                    data-testid="cart-delivery-box-delivery-method-name"
                                >
                                    {getLocalizedValue(name)}
                                </span>
                                {/* Short zone copy repeats the method restriction badge on this row. */}
                                {(isAddressRequired ||
                                    (isUndeliverable && !(areaTimeRestrictionLabel && isTimeRestricted))) && (
                                    <Badge
                                        variant="error"
                                        data-testid="cart-delivery-box-delivery-method-warning"
                                    >
                                        <Icon name="warning" />
                                        {isAddressRequired
                                            ? t('CART_DELIVERY_BOX.ADDRESS_REQUIRED')
                                            : (areaTimeRestrictionLabel ??
                                              t('CART_DELIVERY_BOX.ADDRESS_UNDELIVERABLE'))}
                                    </Badge>
                                )}
                                {isMinOrderDisabled && minPrice && (
                                    <Badge
                                        variant="error"
                                        data-testid="cart-delivery-box-delivery-method-min-order"
                                    >
                                        {t('CART_DELIVERY_BOX.MIN_ORDER_VALUE', {
                                            price: getPriceValueTranslation({
                                                amount: parseFloat(minPrice.amount),
                                                currency: selectedCurrency,
                                                t,
                                            }),
                                        })}
                                    </Badge>
                                )}
                                {isTimeRestricted && (
                                    <Badge
                                        variant="error"
                                        data-testid="cart-delivery-box-delivery-method-time-restriction"
                                    >
                                        {t('CART_DELIVERY_BOX.METHOD_UNAVAILABLE_TRY_LATER')}
                                    </Badge>
                                )}
                                {!isAddressRequired && !isUndeliverable && (
                                    <span
                                        className={cn(
                                            'cart-delivery-box__price',
                                            !totalPrice && 'cart-delivery-box__price--free',
                                        )}
                                        data-testid="cart-delivery-box-delivery-method-price"
                                    >
                                        {totalPrice
                                            ? getPriceValueTranslation({
                                                  t,
                                                  amount: totalPrice,
                                                  currency: selectedCurrency,
                                              })
                                            : t('WORDS.FREE')}
                                    </span>
                                )}
                            </InputRow>
                        );
                    })}
                </>
            )}
        </Form.RadioGroup>
    );
};
